import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { DetailedList, FormBlock, FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { Box, Typography, useTheme } from '@qbe/ads-core';
import React, { ChangeEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';

import CoverStart from '~/components/CoverStart';
import DutyOfDisclosure from '~/components/DutyOfDisclosure';
import IndustrySelector from '~/components/IndustrySelector';
import OccupationCategorySelector from '~/components/OccupationCategorySelector';
import OccupationSelector from '~/components/OccupationSelector';
import PartnerBanner from '~/components/PartnerBanner';
import QuickSearchOccupationSelector from '~/components/QuickSearchOccupationSelector';
import { OccupationDescription } from '~/components/QuickSearchOccupationSelector/OccupationDescription';
import SpecificBusinessDetails from '~/components/SpecificBusinessDetails';
import Modal from '~/components-ds/Modal';
import {
  useAppContent,
  useAppDetails,
  useAppDispatch,
  useAppSelector,
  useBusinessDetails,
  useQuote,
} from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { selectIsReferral } from '~/selectors/selectAppDetails';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import {
  updateIndustry,
  updateOccupation,
  updateOccupationCategory,
  updateOccupationSearchType,
} from '~/slices/businessDetails';
import { getOccupationListAction } from '~/slices/businessDetails/thunks';
import { updateQuoteRetrievalError } from '~/slices/quote';
import { qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { occupationSearchTypes } from '~/types/analytics';
import { OccupationSearchType } from '~/types/refData';
import { analyticsConfig } from '~/utils/analytics/analyticsConfig';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { PropertyDetails } from './PropertyDetails';
import { WhatToInsure } from './WhatToInsure';

const BusinessDetails: React.FC = () => {
  const theme = useTheme();
  const {
    dutyOfDisclosure,
    industry,
    occupationCategory,
    occupation,
    occupationItem,
    occupationSearchType,
    insuredType,
    propertyType,
    coverStart,
  } = useBusinessDetails();
  const dispatch = useAppDispatch();
  const { isInitialized } = useAppDetails();
  const { getTextContent } = useAppContent({
    basePath: 'dsme',
  });

  const { getTextContent: getSharedText } = useAppContent({
    basePath: 'shared',
  });

  useEffect(() => {
    // Trigger occupations list prefetch
    if (coverStart && isInitialized) {
      dispatch(getOccupationListAction());
    }
  }, [coverStart, isInitialized, dispatch]);

  const isPropertyOwner = useSelector(selectIsPropertyOwner);
  const isReferral = useAppSelector(selectIsReferral);

  const { quoteRetrievalError } = useQuote();

  const navigate = useAppNavigate();

  const onClose = () => {
    dispatch(updateQuoteRetrievalError(false));
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const analyticsOccupationSearchHandler = (val: string | null) => {
    const { occupationSearch } = analyticsConfig;

    if (val === 'Icon') {
      // Quick Search Success user switch to icon search.
      if (occupationSearch === occupationSearchTypes.quickSearchSuccess) {
        dispatch(
          qalTrackEventAction({
            event: 'occupation_search',
            eventAction: 'icon search - successful quick search switch to icon search',
            eventLabel: 'icon search',
          })
        );
        analyticsConfig.occupationSearch = occupationSearchTypes.iconSearch;
      }
    }

    // Icon Search without interacting with the quick search field
    if (occupationSearch === '') {
      dispatch(
        qalTrackEventAction({
          event: 'occupation_search',
          eventAction: 'icon search - without interacting quick search',
          eventLabel: 'icon search',
        })
      );
      analyticsConfig.occupationSearch = occupationSearchTypes.iconSearch;
    }
    handleFormStart();
  };

  const handleOccupationSearchTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value as OccupationSearchType;

    dispatch(updateIndustry(undefined));
    dispatch(updateOccupationCategory(undefined));
    dispatch(updateOccupation(undefined));
    dispatch(updateOccupationSearchType(val));
    analyticsOccupationSearchHandler(val);
  };

  useEffect(() => {
    if (insuredType) {
      if (insuredType === 'landlord') {
        navigate('#property-details');
      } else {
        navigate('#what-does-your-business-do');
      }
    }
  }, [dispatch, insuredType, navigate]);

  useEffect(() => {
    if (propertyType) {
      navigate('#what-does-your-business-do');
    }
  }, [navigate, propertyType]);

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <>
      <FormCard
        data-testid="business-details"
        title={getTextContent('businessDetails.title')}
        variant="pageTitle"
        description={getTextContent('businessDetails.description')}
        customElement={isReferral ? <PartnerBanner /> : null}
      />

      <FormCard variant="content">
        <CoverStart />
      </FormCard>

      <FormCard variant="content">
        <DutyOfDisclosure onContinue={() => navigate('#what-to-insure')} />
      </FormCard>

      <FormCard id="what-to-insure" variant="content" disableContent={!dutyOfDisclosure}>
        <WhatToInsure disableContent={!dutyOfDisclosure} />
      </FormCard>

      {insuredType === 'landlord' && (
        <FormCard
          id="property-details"
          variant="sectionTitle"
          title={getTextContent('businessDetailsPropertyOccupancy.title')}
        >
          <PropertyDetails />
        </FormCard>
      )}

      {(!!propertyType || insuredType === 'business-operator') && (
        <>
          <FormCard
            id="what-does-your-business-do"
            variant="sectionTitle"
            disableContent={!dutyOfDisclosure}
            title={
              isPropertyOwner
                ? getTextContent('businessDetailsOccupancySearch.fields.title.landlord')
                : getTextContent('businessDetailsOccupancySearch.fields.title.businessOperator')
            }
          >
            <FormBlock preset="toggle" maxWidth="100%">
              <Toggle
                title=""
                titleHidden
                labelProps={{
                  legendElementOveride: true,
                  typographyOverride: 'h2',
                }}
                name="occupationSearch"
                optionList={[
                  {
                    id: 'industry-icon',
                    label: getTextContent(
                      'businessDetailsOccupancySearch.fields.searchToggle.iconSearch'
                    ),
                    value: OccupationSearchType.Icon,
                    defaultChecked:
                      occupationSearchType === OccupationSearchType.Icon ||
                      occupationSearchType === undefined,
                    onChange: handleOccupationSearchTypeChange,
                    'data-track': 'Industry icons',
                  },
                  {
                    id: 'quick-search-icon',
                    label: getTextContent(
                      'businessDetailsOccupancySearch.fields.searchToggle.quickSearch'
                    ),
                    value: OccupationSearchType.QuickSearch,
                    defaultChecked: occupationSearchType === OccupationSearchType.QuickSearch,
                    'data-track': 'Quick search',
                    onChange: handleOccupationSearchTypeChange,
                  },
                ]}
                size="small"
              />
            </FormBlock>

            {occupationSearchType === OccupationSearchType.Icon ? (
              <>
                <Box id="industry" sx={{ mb: 4 }}>
                  <IndustrySelector onSelect={() => navigate('#occupationCategory')} />
                </Box>
                {industry && (
                  <Box
                    id="occupationCategory"
                    sx={{
                      mt: 2,
                      mb: 4,
                    }}
                  >
                    <OccupationCategorySelector onSelect={() => navigate('#occupation')} />
                  </Box>
                )}

                {occupationCategory && (
                  <Box id="occupation" sx={{ mt: 2 }} width="100%">
                    <OccupationSelector onSelect={() => navigate('#specific-business-details')} />
                  </Box>
                )}
              </>
            ) : (
              <QuickSearchOccupationSelector />
            )}
          </FormCard>

          <FormCard
            variant="sectionTitle"
            title={
              isPropertyOwner
                ? getTextContent('businessDetailsPropertyDetails.title')
                : getTextContent('businessDetailsSpecificBusinessDetails.title')
            }
            id="specific-business-details"
            disableContent={!occupation}
          >
            {isPropertyOwner && (
              <Box id="occupation-review">
                <Typography gutterBottom textAlign="center">
                  {isPropertyOwner
                    ? getTextContent('businessDetailsPropertyDetails.description')
                    : getTextContent('businessDetailsSpecificBusinessDetails.description')}
                </Typography>

                {occupation?.occupation && (
                  <DetailedList
                    list={[
                      {
                        id: 'propertyOwnerOccupation',
                        label: getTextContent(
                          'businessDetailsPropertyDetails.fields.occupation.yourOccupation'
                        ),
                        name: 'occupation',
                        value: 'Property owner',
                      },
                      {
                        id: 'tenantOccupation',
                        label: getTextContent(
                          'businessDetailsPropertyDetails.fields.occupation.tenantsOccupation'
                        ),
                        name: 'tenantOccupation',
                        value: occupation?.occupation,
                      },
                    ]}
                    variant="multiple"
                    title={getTextContent('businessDetailsPropertyDetails.fields.occupation.title')}
                  />
                )}

                {occupationItem && occupationSearchType === OccupationSearchType.QuickSearch && (
                  <OccupationDescription
                    occupationItem={occupationItem}
                    heading="Tenant's occupation description"
                  />
                )}

                <Typography textAlign="center">
                  {getTextContent('businessDetailsPropertyDetails.fields.occupation.description')}
                </Typography>
              </Box>
            )}
            <SpecificBusinessDetails />
          </FormCard>
        </>
      )}

      <Modal
        id="retrieve-quote-error-modal"
        analyticsModalName="header retrieve quote error modal"
        open={Boolean(quoteRetrievalError)}
        title={getTextContent('retrieveQuote.fields.retrieveQuoteErrorModal.title')}
        iconProps={{
          muiIcon: InfoOutlined,
          // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445

          // @ts-expect-error
          color: theme.color.icon.info,
        }}
        primaryBtnProps={{
          label: getSharedText('continueModalButtonLabel'),
          'data-track': 'back to quote',
          onClick: onClose,
        }}
      >
        <Typography>
          {getTextContent('retrieveQuote.fields.retrieveQuoteErrorModal.description')}
        </Typography>
      </Modal>
    </>
  );
};

export default BusinessDetails;
